import { useQuery } from '@tanstack/react-query';
import { getAbiItem, getAddress, type Address } from 'viem';
import { usePublicClient } from 'wagmi';
import { inviteCode, normalizeInviteCode, referralManagerAbi } from '@syntra/abi';
import { DEPLOYMENT as D, INDEXER_URL } from '../config/env.ts';
import { pagedLogs } from '../lib/logs.ts';

/**
 * The member an invite code belongs to: the indexer's /invite lookup, or (no indexer, or it is unreachable) the
 * ReferralManager's Bound logs. `null` once looked up and nobody has the code.
 */
export function useInviteSponsor(code: string | null) {
  const client = usePublicClient();
  return useQuery({
    queryKey: ['invite', code],
    enabled: Boolean(code && client),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Address | null> => {
      if (INDEXER_URL) {
        const res = await fetch(`${INDEXER_URL}/invite/${code}`).catch(() => null);
        if (res?.status === 404) return null;
        if (res?.ok) return getAddress(((await res.json()) as { address: string }).address);
      }
      const logs = await pagedLogs(await client!.getBlockNumber(), BigInt(D.deployedAtBlock ?? 0), (fromBlock, toBlock) =>
        client!.getLogs({ address: D.referral, event: getAbiItem({ abi: referralManagerAbi, name: 'Bound' }), fromBlock, toBlock }),
      );
      const hit = logs.find((l) => l.args.user && inviteCode(l.args.user) === code); // logs come oldest first: the first bind keeps a code
      return hit?.args.user ? getAddress(hit.args.user) : null;
    },
  });
}

// An invite link (/r/<code>/<side>) is remembered on this device until the wallet has joined, so leaving the referral
// page first (to stake, say) does not lose it. A first stake made before joining binds the wallet to the default
// sponsor for good, so the staking actions ask to join the remembered referrer first (components/tx/JoinFirst.tsx).
const INVITE_KEY = 'syntra.invite';
export type InviteSide = 'L' | 'R';

export function rememberInvite(code: string, side: InviteSide) {
  try {
    localStorage.setItem(INVITE_KEY, JSON.stringify({ code, side }));
  } catch {
    /* private mode or storage blocked: the URL still carries it on the referral page */
  }
}

export function storedInvite(): { code: string; side: InviteSide } | null {
  try {
    const v = JSON.parse(localStorage.getItem(INVITE_KEY) ?? 'null') as { code?: string; side?: string } | null;
    const code = v?.code ? normalizeInviteCode(v.code) : null;
    return code ? { code, side: v?.side === 'R' ? 'R' : 'L' } : null;
  } catch {
    return null;
  }
}

export function forgetInvite() {
  try {
    localStorage.removeItem(INVITE_KEY);
  } catch {
    /* nothing to forget */
  }
}
